import React, { useEffect, useState } from 'react'
import Modal from 'components/modal'
import Button from 'components/button'
import api from 'helpers/api'
import { IOrganisation, IOrganisationUser } from 'types'
import { useApplicationStore } from 'hooks/application'
import {
  OrganisationRoles,
  ORG_PLAN_TIERS,
  PROJECT,
  HUB_CREATION_RESTRICTION_LABEL,
  HUB_CREATION_RESTRICTION_DESCRIPTION,
  SYNC_CONFIGURATION_RESTRICTION_LABEL,
  SYNC_CONFIGURATION_RESTRICTION_DESCRIPTION
} from 'app-constants'
import Checkbox from 'components/checkbox'
import { capitaliseFirstLetter } from 'helpers/utils'

interface SecuritySettingsModalProps {
  open: boolean
  onClose: () => void
  organisation: IOrganisation
  onChanged?: (organisation: IOrganisation) => void
}

const SecuritySettingsModal: React.FC<SecuritySettingsModalProps> = ({ open, onClose, organisation, onChanged }) => {
  const { displayErrorMessage, setSnackbarMessage } = useApplicationStore()

  const [saving, setSaving] = useState(false)
  const [enabledApiKeys, setEnabledApiKeys] = useState<boolean>(organisation.enableUserApiKeys)
  const [disablePersonalViewSaving, setDisablePersonalViewSaving] = useState<boolean>(
    organisation.disablePersonalViewSaving
  )
  const [restrictHubCreation, setRestrictHubCreation] = useState<boolean>(organisation.restrictHubCreation)
  const [restrictSyncConfiguration, setRestrictSyncConfiguration] = useState<boolean>(
    organisation.restrictSyncConfiguration
  )

  const isScaleUp = organisation.planTier === ORG_PLAN_TIERS.SCALE_UP

  // Derive initial state from organisation when opened
  useEffect(() => {
    if (!open) return
    setEnabledApiKeys(organisation.enableUserApiKeys)
    setDisablePersonalViewSaving(organisation.disablePersonalViewSaving)
    setRestrictHubCreation(organisation.restrictHubCreation)
    setRestrictSyncConfiguration(organisation.restrictSyncConfiguration)
  }, [
    open,
    organisation.enableUserApiKeys,
    organisation.disablePersonalViewSaving,
    organisation.restrictHubCreation,
    organisation.restrictSyncConfiguration
  ])

  // Keep users intact (email + role only)
  const usersPayload: IOrganisationUser[] = (organisation.users || [])
    .filter((u) => (u.user.email || '').trim() !== '')
    .map((u) => ({ user: { email: u.user.email }, role: u.role })) as unknown as IOrganisationUser[]

  const handleSave = async () => {
    setSaving(true)

    const payload: IOrganisation = {
      ...organisation,
      users: usersPayload.filter((u) => u.role !== OrganisationRoles.MEMBER),
      enableUserApiKeys: enabledApiKeys,
      // Enabling this is Scale-Up-only on the backend. For non-Scale-Up orgs we
      // always send `false` so that (a) an unrelated security save doesn't 422
      // because of a stuck-on flag and (b) a plan downgrade self-heals the flag
      // on the next save. The toggle is only rendered for Scale Up so the
      // local state is irrelevant on lower tiers.
      disablePersonalViewSaving: isScaleUp ? disablePersonalViewSaving : false,
      // Scale-Up-only too, and sent as `false` below Scale Up for the same reasons.
      restrictHubCreation: isScaleUp ? restrictHubCreation : false,
      restrictSyncConfiguration: isScaleUp ? restrictSyncConfiguration : false
    }

    api
      .updateOrganisation(organisation.publicId, payload)
      .then((resp) => {
        setSnackbarMessage?.({ status: 'success', message: 'Security settings updated' })
        setSaving(false)
        onChanged?.(resp.data as IOrganisation)
        onClose()
      })
      .catch((e) => {
        displayErrorMessage(e)
        setSaving(false)
      })
  }

  return (
    <Modal id="organisation-security-settings-modal" open={open} setOpen={onClose} title="Security Settings">
      <div className="flex flex-col gap-[30px]">
        <div className="flex flex-col gap-[5px]">
          <label htmlFor="login-provider-enforcement" className="block font-bold mb-[5px]">
            Enable User API Keys
          </label>
          <Checkbox checked={enabledApiKeys} onChange={(e) => setEnabledApiKeys(e.target.checked)}>
            Allow users to use and create API keys for accessing the API
          </Checkbox>
        </div>

        {isScaleUp && (
          <div className="flex flex-col gap-[5px]">
            <div className="block font-bold mb-[5px]">Personal Views</div>
            <Checkbox
              checked={disablePersonalViewSaving}
              onChange={(e) => setDisablePersonalViewSaving(e.target.checked)}
            >
              Disable &ldquo;Save Personal View&rdquo; for unlicensed members across all {PROJECT}s in this organisation
            </Checkbox>
            <div className="text-xs text-muted mt-[5px]">
              Unlicensed members can still apply filters, groups and sorting temporarily, but won&rsquo;t be able to
              save them for future use.
            </div>
            <div className="text-xs text-warning mt-[5px]">
              You&rsquo;ll also lose visibility into how these members are manipulating and viewing the data.
            </div>
          </div>
        )}

        {isScaleUp && (
          <div className="flex flex-col gap-[5px]">
            <div className="block font-bold mb-[5px]">{capitaliseFirstLetter(PROJECT)} Creation</div>
            <Checkbox checked={restrictHubCreation} onChange={(e) => setRestrictHubCreation(e.target.checked)}>
              {HUB_CREATION_RESTRICTION_LABEL}
            </Checkbox>
            <div className="text-xs text-muted mt-[5px]">{HUB_CREATION_RESTRICTION_DESCRIPTION}</div>
          </div>
        )}

        {isScaleUp && (
          <div className="flex flex-col gap-[5px]">
            <div className="block font-bold mb-[5px]">Sync Configuration</div>
            <Checkbox
              checked={restrictSyncConfiguration}
              onChange={(e) => setRestrictSyncConfiguration(e.target.checked)}
            >
              {SYNC_CONFIGURATION_RESTRICTION_LABEL}
            </Checkbox>
            <div className="text-xs text-muted mt-[5px]">{SYNC_CONFIGURATION_RESTRICTION_DESCRIPTION}</div>
          </div>
        )}

        <div className="flex items-center">
          <Button className="mr-2" internalType="danger" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} isLoading={saving} internalType="accept">
            Save
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default SecuritySettingsModal
